import { TextSelection } from "@tiptap/pm/state";
import { Editor } from "@tiptap/core";
import type { Mark } from "@tiptap/pm/model";

/**
 * Get active marks including stored marks (marks that will be applied when typing)
 * This is important for displaying correct toolbar state when cursor is in empty paragraph
 */
export function getActiveMarks(editor: Editor): readonly Mark[] {
  const { state } = editor;
  const { storedMarks, selection } = state;

  // Priority 1: stored marks (set when you press Enter or create empty list)
  if (storedMarks && storedMarks.length > 0) {
    return storedMarks;
  }

  // Priority 2: If there's a selection (not just cursor), get marks from the actual content
  if (!selection.empty) {
    const marks: Mark[] = [];
    let firstNode = true;

    state.doc.nodesBetween(selection.from, selection.to, (node) => {
      if (node.isText) {
        if (firstNode) {
          // Get marks from first text node in selection
          marks.push(...node.marks);
          firstNode = false;
        }
        return false; // Don't traverse into child nodes
      }
    });

    if (marks.length > 0) {
      return marks;
    }
  }

  // Priority 3: marks at cursor position
  return selection.$from.marks();
}

export function expandSelectionToWord(editor: Editor) {
  const { state, view } = editor;
  const { empty } = state.selection;

  // Platí jen pro prázdnou selection (kurzor)
  if (!empty) return;

  const $from = state.selection.$from;
  const textNode = $from.parent;

  if (!textNode.isTextblock) return;

  const text = textNode.textContent;
  const offset = $from.parentOffset;

  // Pokud je na začátku nebo na konci text blocku
  if (offset <= 0 || offset >= text.length) return;

  const charBefore = text[offset - 1];
  const charAfter = text[offset];

  // ✅ Expanduj jen pokud je kurzor opravdu *uvnitř* slova
  if (/\s/.test(charBefore) || /\s/.test(charAfter)) {
    // Na hranici — neexpandovat
    return;
  }

  // Najdi začátek slova
  let start = offset;
  while (start > 0 && /\S/.test(text[start - 1])) {
    start--;
  }

  // Najdi konec slova
  let end = offset;
  while (end < text.length && /\S/.test(text[end])) {
    end++;
  }

  // Vytvoř novou selection
  const wordFrom = $from.start() + start;
  const wordTo = $from.start() + end;

  if (wordFrom < wordTo) {
    const transaction = state.tr.setSelection(
      TextSelection.create(state.doc, wordFrom, wordTo)
    );
    view.dispatch(transaction);
  }
}
